import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  GraduationCap, LogOut, CheckCircle, Clock, Lock, 
  ChevronRight, BookOpen, FileText, Play, AlertCircle,
  Award, Loader2, Send, User, Check, ArrowLeft, Trophy,
  Star, Target, Zap, Rocket, Medal, ChevronDown, Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/training`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_omni-solutions-1/artifacts/nc5fgtrv_Logo.jpg";

const TrainingPortal = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [daysStatus, setDaysStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null);
  const [dayContent, setDayContent] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);
  
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [readSections, setReadSections] = useState(new Set());
  const [expandedSection, setExpandedSection] = useState("section-0");
  
  // HR Handbook PDF state
  const [handbookPdf, setHandbookPdf] = useState(null);
  const [loadingHandbook, setLoadingHandbook] = useState(false);
  const [hrHandbookUploaded, setHrHandbookUploaded] = useState(false);
  
  // Credentials state (CRM & Email)
  const [credentials, setCredentials] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(false);

  const getToken = () => localStorage.getItem("training_token");
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  useEffect(() => {
    if (!getToken()) { navigate("/training/login"); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, statusRes] = await Promise.all([
        axios.get(`${API}/profile`, authHeaders()),
        axios.get(`${API}/days/status`, authHeaders())
      ]);
      setProfile(profileRes.data);
      setDaysStatus(statusRes.data.days || []);
      setHrHandbookUploaded(statusRes.data.hr_handbook_uploaded || false);
      
      // Fetch credentials if all days are completed
      if (statusRes.data.all_completed && statusRes.data.all_quizzes_passed) {
        fetchCredentials();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("training_token");
        navigate("/training/login");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCredentials = async () => {
    setLoadingCredentials(true);
    try {
      const response = await axios.get(`${API}/credentials`, authHeaders());
      setCredentials(response.data);
    } catch (error) {
      console.error("Error fetching credentials:", error);
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("training_token");
    localStorage.removeItem("training_candidate");
    toast.success("Logged out successfully");
    navigate("/training/login");
  };

  const loadDayContent = async (dayNumber) => {
    setLoadingDay(true);
    setActiveDay(dayNumber);
    setCurrentSectionIndex(0);
    setReadSections(new Set());
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizResult(null);
    setExpandedSection("section-0");
    setHandbookPdf(null);
    
    try {
      const response = await axios.get(`${API}/day/${dayNumber}`, authHeaders());
      if (response.data.locked) {
        toast.error(response.data.message);
        setDayContent(null);
        setActiveDay(null);
        return;
      }
      setDayContent(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to load Day ${dayNumber}`);
      setActiveDay(null);
    } finally {
      setLoadingDay(false);
    }
  };

  const loadHandbookPdf = async () => {
    if (handbookPdf) return; // Already loaded
    setLoadingHandbook(true);
    try {
      const response = await axios.get(`${API}/handbook`, authHeaders());
      setHandbookPdf(response.data);
    } catch (error) {
      console.error("Error loading handbook:", error);
      toast.error(error.response?.data?.detail || "HR Handbook not available yet");
    } finally {
      setLoadingHandbook(false);
    }
  };

  const handleSectionClick = (idx) => {
    setShowQuiz(false);
    setCurrentSectionIndex(idx);
    setExpandedSection(`section-${idx}`);
    if (idx > 0) {
      setReadSections(prev => {
        const newSet = new Set(prev);
        for (let i = 0; i < idx; i++) newSet.add(i);
        return newSet;
      });
    }
    // Load handbook PDF if this is the HR Handbook section
    const section = dayContent?.sections?.[idx];
    if (section?.type === 'pdf_document' && section?.requires_pdf_upload) {
      loadHandbookPdf();
    }
  };

  const handleNextSection = () => {
    if (dayContent && currentSectionIndex < dayContent.sections.length - 1) {
      setReadSections(prev => new Set([...prev, currentSectionIndex]));
      const next = currentSectionIndex + 1;
      setCurrentSectionIndex(next);
      setExpandedSection(`section-${next}`);
    } else if (dayContent) {
      setReadSections(prev => new Set([...prev, currentSectionIndex]));
    }
  };

  const handlePreviousSection = () => {
    if (currentSectionIndex > 0) {
      const prev = currentSectionIndex - 1;
      setCurrentSectionIndex(prev);
      setExpandedSection(`section-${prev}`);
    }
  };

  const allSectionsRead = dayContent && readSections.size >= dayContent.sections.length - 1;
  const currentSection = dayContent?.sections?.[currentSectionIndex];

  const handleQuizAnswer = (questionId, answerIndex) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const handleSubmitQuiz = async () => {
    if (!dayContent || !activeDay) return;
    const unanswered = dayContent.quiz.questions.filter(q => quizAnswers[q.id] === undefined);
    
    if (unanswered.length > 0) {
      toast.error(`${unanswered.length} question(s) unanswered`);
      return;
    }
    
    setSubmittingQuiz(true);
    try {
      const response = await axios.post(`${API}/day/${activeDay}/quiz`, quizAnswers, authHeaders());
      setQuizResult(response.data);
      if (response.data.passed) {
        toast.success("Congratulations! Quiz passed!");
        fetchData();
      } else {
        toast.error(`Score: ${response.data.score}% - Need ${response.data.passing_score}%`);
      }
    } catch (error) {
      toast.error("Failed to submit");
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleSendOtp = async () => {
    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/handbook/send-otp`, {}, authHeaders());
      setDebugOtp(response.data.debug_otp);
      setShowOtpModal(true);
      toast.success(response.data.email_sent ? "OTP sent to your email!" : "OTP generated");
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter 6-digit OTP"); return; }
    
    setVerifyingOtp(true);
    try {
      const formData = new FormData();
      formData.append("otp", otp);
      await axios.post(`${API}/handbook/verify-otp`, formData, authHeaders());
      toast.success("HR Handbook signed successfully!");
      setShowOtpModal(false);
      setOtp("");
      fetchData();
      if (dayContent) setDayContent({ ...dayContent, handbook_signed: true });
    } catch (error) {
      toast.error("Invalid OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const formatCountdown = (isoString) => {
    if (!isoString) return null;
    const diff = new Date(isoString) - new Date();
    if (diff <= 0) return "Now";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getProgressPercent = () => {
    if (!daysStatus.length) return 0;
    const completed = daysStatus.filter(d => d.completed).length;
    const handbookBonus = profile?.handbook_signed ? 0.5 : 0;
    return Math.round(((completed + handbookBonus) / 3.5) * 100);
  };

  const getDayIcon = (day) => {
    if (day === 1) return <Target className="w-5 h-5" />;
    if (day === 2) return <Zap className="w-5 h-5" />;
    return <Rocket className="w-5 h-5" />;
  };

  const getDayColor = (day, completed) => {
    if (completed) return "bg-green-500";
    if (day === 1) return "bg-blue-500";
    if (day === 2) return "bg-purple-500";
    return "bg-orange-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a8a] mx-auto mb-4" />
          <p className="text-slate-600">Loading your training...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Omnilink Solutions" className="h-10" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-slate-900">Training Portal</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{profile?.full_name}</p>
                <p className="text-xs text-slate-500">{profile?.designation}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="training-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {!activeDay ? (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Welcome Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-[#1e3a8a] to-[#4f46e5] rounded-xl p-6 mb-8 text-white relative overflow-hidden"
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>
                
                <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-6 h-6" />
                      <span className="text-blue-200 text-sm font-medium">Training Journey</span>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold mb-2">Welcome back, {profile?.full_name?.split(' ')[0]}! 🚀</h2>
                    <p className="text-blue-100">
                      Complete all modules to earn your certification
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold">{getProgressPercent()}%</div>
                      <div className="text-blue-200 text-sm">Complete</div>
                    </div>
                    <div className="w-20 h-20 relative">
                      <svg className="w-20 h-20 -rotate-90">
                        <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                        <circle 
                          cx="40" cy="40" r="36" fill="none" 
                          stroke="white" strokeWidth="6" strokeLinecap="round"
                          strokeDasharray={226}
                          strokeDashoffset={226 - (226 * getProgressPercent() / 100)}
                          className="transition-all duration-1000"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Trophy className="w-8 h-8 text-yellow-300" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {profile?.admin_approved && (
                  <div className="relative mt-4 p-3 bg-white/10 rounded-lg flex items-center gap-3">
                    <Medal className="w-5 h-5 text-yellow-300" />
                    <span className="font-medium">Training Approved! You&apos;re ready to start.</span>
                  </div>
                )}
              </motion.div>

              {/* Training Roadmap */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Your Training Roadmap
                </h3>
                
                <div className="relative">
                  {/* Connection Line */}
                  <div className="absolute top-24 left-1/2 w-[calc(100%-200px)] h-1 bg-slate-200 -translate-x-1/2 hidden lg:block" />
                  
                  <div className="grid lg:grid-cols-3 gap-6">
                    {daysStatus.map((day, index) => (
                      <motion.div
                        key={day.day}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card 
                          className={`relative border-2 transition-all cursor-pointer hover:shadow-lg ${
                            day.completed 
                              ? 'border-green-200 bg-green-50/50' 
                              : day.unlocked 
                                ? 'border-[#1e3a8a] hover:border-[#1e3a8a]' 
                                : 'border-slate-200 opacity-60'
                          }`}
                          onClick={() => day.unlocked && loadDayContent(day.day)}
                          data-testid={`training-day-${day.day}-card`}
                        >
                          {/* Day Badge */}
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                            <div className={`w-12 h-12 rounded-full ${getDayColor(day.day, day.completed)} text-white flex items-center justify-center shadow-lg`}>
                              {day.completed ? <CheckCircle className="w-6 h-6" /> : getDayIcon(day.day)}
                            </div>
                          </div>
                          
                          <CardContent className="pt-10 pb-6 px-6">
                            <div className="text-center mb-4">
                              <Badge variant={day.completed ? "default" : day.unlocked ? "secondary" : "outline"} className="mb-2">
                                Day {day.day}
                              </Badge>
                              <h4 className="font-semibold text-slate-900 text-lg">
                                {day.title.replace(`Day ${day.day}: `, '')}
                              </h4>
                            </div>
                            
                            {day.completed ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                  <span className="text-slate-600 text-sm">Quiz Score</span>
                                  <span className="font-bold text-green-600">{day.quiz_score}%</span>
                                </div>
                                {day.handbook_required && (
                                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                    <span className="text-slate-600 text-sm">HR Handbook</span>
                                    <Badge variant={day.handbook_signed ? "default" : "destructive"}>
                                      {day.handbook_signed ? 'Signed' : 'Pending'}
                                    </Badge>
                                  </div>
                                )}
                                <Button variant="outline" className="w-full" onClick={() => loadDayContent(day.day)}>
                                  Review Content
                                </Button>
                              </div>
                            ) : day.unlocked ? (
                              <div className="space-y-3">
                                <p className="text-slate-500 text-sm text-center">
                                  Ready to begin your training
                                </p>
                                <Button className="w-full bg-[#1e3a8a] hover:bg-[#172554]">
                                  Start Day {day.day} <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </div>
                            ) : (
                              <div className="text-center space-y-2">
                                <Lock className="w-8 h-8 text-slate-300 mx-auto" />
                                <p className="text-slate-400 text-sm">
                                  {day.requires_previous 
                                    ? 'Complete previous day first' 
                                    : day.hours_remaining 
                                      ? `Unlocks in ${day.hours_remaining} hours`
                                      : `Unlocks in ${formatCountdown(day.unlock_time)}`}
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="border">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-[#1e3a8a]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {daysStatus.filter(d => d.completed).length}/3
                      </p>
                      <p className="text-sm text-slate-500">Days Completed</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {daysStatus.filter(d => d.quiz_passed).length}/3
                      </p>
                      <p className="text-sm text-slate-500">Quizzes Passed</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {profile?.handbook_signed ? '✓' : '−'}
                      </p>
                      <p className="text-sm text-slate-500">HR Handbook</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* CRM & Email Credentials Section - Shows after completing all 3 days */}
              {daysStatus.every(d => d.completed && d.quiz_passed) && (
                <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <Trophy className="w-5 h-5" />
                      Training Complete - Your Work Credentials
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingCredentials ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                        <span className="ml-2 text-slate-600">Loading credentials...</span>
                      </div>
                    ) : credentials?.available ? (
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* CRM Credentials */}
                        <div className="bg-white p-5 rounded-xl border shadow-sm">
                          <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Target className="w-4 h-4 text-blue-600" />
                            </div>
                            CRM Platform
                          </h4>
                          {credentials.crm_credentials?.platform_name ? (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-slate-500 uppercase tracking-wide">Platform</label>
                                <p className="font-medium text-slate-800">{credentials.crm_credentials.platform_name}</p>
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase tracking-wide">URL</label>
                                <a 
                                  href={credentials.crm_credentials.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline block truncate"
                                >
                                  {credentials.crm_credentials.url}
                                </a>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-slate-500 uppercase tracking-wide">Username</label>
                                  <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{credentials.crm_credentials.username}</p>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500 uppercase tracking-wide">Password</label>
                                  <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{credentials.crm_credentials.password}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-slate-500 text-sm">Not configured yet</p>
                          )}
                        </div>
                        
                        {/* Email Credentials */}
                        <div className="bg-white p-5 rounded-xl border shadow-sm">
                          <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Send className="w-4 h-4 text-purple-600" />
                            </div>
                            Email Platform
                          </h4>
                          {credentials.email_credentials?.platform_name ? (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-slate-500 uppercase tracking-wide">Platform</label>
                                <p className="font-medium text-slate-800">{credentials.email_credentials.platform_name}</p>
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase tracking-wide">URL</label>
                                <a 
                                  href={credentials.email_credentials.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline block truncate"
                                >
                                  {credentials.email_credentials.url}
                                </a>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-slate-500 uppercase tracking-wide">Username</label>
                                  <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{credentials.email_credentials.username}</p>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500 uppercase tracking-wide">Password</label>
                                  <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{credentials.email_credentials.password}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-slate-500 text-sm">Not configured yet</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">
                          {credentials?.message || "Your credentials will be added by admin shortly."}
                        </p>
                        <p className="text-slate-500 text-sm mt-1">Please check back later or contact your supervisor.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </motion.div>
          ) : loadingDay ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center py-32"
            >
              <Loader2 className="w-8 h-8 animate-spin text-[#1e3a8a]" />
            </motion.div>
          ) : dayContent && (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Day Header */}
              <div className="mb-6">
                <Button 
                  variant="ghost" 
                  onClick={() => { setActiveDay(null); setDayContent(null); }}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Overview
                </Button>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="mb-2">Day {activeDay}</Badge>
                    <h2 className="text-2xl font-bold text-slate-900">{dayContent.title}</h2>
                    <p className="text-slate-500">{dayContent.description}</p>
                  </div>
                  {dayContent.completed && (
                    <Badge className="bg-green-100 text-green-700 text-sm px-4 py-2">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Completed
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Sections Sidebar */}
                <div className="lg:col-span-1">
                  <Card className="border sticky top-24">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-[#1e3a8a]" />
                        Modules
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {dayContent.sections.map((section, idx) => (
                          <button
                            key={section.id}
                            onClick={() => handleSectionClick(idx)}
                            className={`w-full p-4 text-left flex items-center gap-3 transition-colors ${
                              currentSectionIndex === idx && !showQuiz
                                ? 'bg-blue-50 border-l-4 border-[#1e3a8a]' 
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              readSections.has(idx) || (currentSectionIndex === idx && !showQuiz)
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {readSections.has(idx) ? <Check className="w-4 h-4" /> : idx + 1}
                            </div>
                            <span className={`text-sm ${currentSectionIndex === idx && !showQuiz ? 'font-medium text-[#1e3a8a]' : 'text-slate-700'}`}>
                              {section.title}
                            </span>
                          </button>
                        ))}
                        
                        {/* Quiz Button */}
                        {(allSectionsRead || dayContent.completed) && (
                          <button
                            onClick={() => setShowQuiz(true)}
                            className={`w-full p-4 text-left flex items-center gap-3 transition-colors ${
                              showQuiz ? 'bg-purple-50 border-l-4 border-purple-600' : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              dayContent.quiz_passed ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {dayContent.quiz_passed ? <Check className="w-4 h-4" /> : <Award className="w-4 h-4" />}
                            </div>
                            <span className={`text-sm ${showQuiz ? 'font-medium text-purple-700' : 'text-slate-700'}`}>
                              Day {activeDay} Quiz
                            </span>
                          </button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Content Area */}
                <div className="lg:col-span-2">
                  {!showQuiz ? (
                    <Card className="border">
                      <CardHeader className="border-b bg-slate-50">
                        <div className="flex items-center gap-3">
                          {currentSection?.type === "document" ? (
                            <FileText className="w-5 h-5 text-[#1e3a8a]" />
                          ) : currentSection?.type === "video" ? (
                            <Play className="w-5 h-5 text-[#1e3a8a]" />
                          ) : (
                            <BookOpen className="w-5 h-5 text-[#1e3a8a]" />
                          )}
                          <CardTitle className="text-lg">{currentSection?.title}</CardTitle>
                        </div>
                        {currentSection?.requires_signature && !dayContent.handbook_signed && (
                          <Badge variant="destructive" className="w-fit mt-2">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            E-Signature Required
                          </Badge>
                        )}
                      </CardHeader>
                      
                      <CardContent className="p-6">
                        {/* PDF Document View for HR Handbook */}
                        {currentSection?.type === 'pdf_document' && currentSection?.requires_pdf_upload ? (
                          <div className="space-y-6">
                            {loadingHandbook ? (
                              <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-[#1e3a8a] mb-4" />
                                <p className="text-slate-500">Loading HR Handbook...</p>
                              </div>
                            ) : handbookPdf ? (
                              <>
                                {/* PDF Viewer - View Only, No Download */}
                                <div className="border rounded-lg overflow-hidden bg-slate-100">
                                  <div className="bg-slate-200 px-4 py-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-5 h-5 text-red-600" />
                                      <span className="font-medium text-sm">{handbookPdf.filename}</span>
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      View Only
                                    </Badge>
                                  </div>
                                  <iframe
                                    src={`data:application/pdf;base64,${handbookPdf.data}#toolbar=0&navpanes=0&scrollbar=1`}
                                    className="w-full h-[600px]"
                                    title="HR Handbook"
                                  />
                                </div>
                                <p className="text-xs text-slate-500 text-center">
                                  Note: This document is for viewing only and cannot be downloaded.
                                </p>
                              </>
                            ) : (
                              <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed">
                                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h4 className="font-medium text-slate-700 mb-2">HR Handbook Not Available</h4>
                                <p className="text-slate-500 text-sm">
                                  The HR Handbook has not been uploaded by the admin yet.
                                  <br />Please check back later or contact HR.
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Regular HTML Content */
                          <div 
                            className="prose prose-slate max-w-none
                              prose-headings:font-semibold
                              prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:text-slate-900
                              prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2 prose-h3:text-[#1e3a8a]
                              prose-p:text-slate-600 prose-p:leading-relaxed
                              prose-li:text-slate-600
                              prose-strong:text-slate-900"
                            dangerouslySetInnerHTML={{ __html: currentSection?.content || "" }}
                          />
                        )}
                        
                        {/* HR Handbook Signature */}
                        {currentSection?.requires_signature && handbookPdf && (
                          <div className="mt-8 p-6 rounded-xl bg-amber-50 border border-amber-200">
                            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-amber-600" />
                              E-Signature Required
                            </h4>
                            {dayContent.handbook_signed || handbookPdf?.signed ? (
                              <div className="flex items-center gap-2 text-green-700">
                                <CheckCircle className="w-5 h-5" />
                                <span>You have signed the HR Handbook</span>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-slate-600 text-sm">
                                  By signing, you acknowledge that you have read and agree to comply with all policies in this HR Handbook.
                                </p>
                                <Button 
                                  onClick={handleSendOtp}
                                  disabled={sendingOtp}
                                  className="bg-amber-600 hover:bg-amber-700"
                                  data-testid="send-handbook-otp-btn"
                                >
                                  {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                  Sign with OTP
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Navigation */}
                        <div className="flex items-center justify-between mt-8 pt-6 border-t">
                          <Button 
                            variant="outline"
                            onClick={handlePreviousSection}
                            disabled={currentSectionIndex === 0}
                          >
                            Previous
                          </Button>
                          {currentSectionIndex < dayContent.sections.length - 1 ? (
                            <Button onClick={handleNextSection} className="bg-[#1e3a8a] hover:bg-[#172554]">
                              Next Module <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          ) : (
                            <Button 
                              onClick={() => { handleNextSection(); setShowQuiz(true); }}
                              className="bg-purple-600 hover:bg-purple-700"
                              data-testid="take-quiz-btn"
                            >
                              Take Quiz <Award className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    /* Quiz */
                    <Card className="border">
                      <CardHeader className="border-b bg-purple-50">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Award className="w-5 h-5 text-purple-600" />
                          Day {activeDay} Quiz
                        </CardTitle>
                        <p className="text-slate-500 text-sm">Score {dayContent.quiz.passing_score}% to pass</p>
                      </CardHeader>
                      
                      <CardContent className="p-6">
                        {quizResult ? (
                          <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-8"
                          >
                            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
                              quizResult.passed ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              <span className={`text-3xl font-bold ${quizResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                                {quizResult.score}%
                              </span>
                            </div>
                            <h3 className={`text-xl font-semibold mb-2 ${quizResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                              {quizResult.passed ? '🎉 Congratulations!' : 'Not quite yet'}
                            </h3>
                            <p className="text-slate-500 mb-2">{quizResult.message}</p>
                            <p className="text-slate-400 text-sm">{quizResult.correct_count}/{quizResult.total_questions} correct</p>
                            <Button 
                              onClick={() => quizResult.passed ? (setActiveDay(null), setDayContent(null)) : (setQuizResult(null), setQuizAnswers({}))}
                              className="mt-6"
                            >
                              {quizResult.passed ? 'Continue Training' : 'Retry Quiz'}
                            </Button>
                          </motion.div>
                        ) : (
                          <div className="space-y-6">
                            {dayContent.quiz.questions.map((q, qIdx) => (
                              <div key={q.id} className="p-4 bg-slate-50 rounded-xl">
                                <h4 className="font-medium text-slate-900 mb-4">
                                  <span className="text-[#1e3a8a] mr-2">Q{qIdx + 1}.</span>
                                  {q.question}
                                </h4>
                                <div className="space-y-2">
                                  {q.options.map((option, oIdx) => (
                                    <label
                                      key={oIdx}
                                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                                        quizAnswers[q.id] === oIdx 
                                          ? 'border-[#1e3a8a] bg-blue-50' 
                                          : 'border-slate-200 bg-white hover:bg-slate-50'
                                      }`}
                                    >
                                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                        quizAnswers[q.id] === oIdx ? 'border-[#1e3a8a] bg-[#1e3a8a]' : 'border-slate-300'
                                      }`}>
                                        {quizAnswers[q.id] === oIdx && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      <input type="radio" name={q.id} checked={quizAnswers[q.id] === oIdx} onChange={() => handleQuizAnswer(q.id, oIdx)} className="sr-only" />
                                      <span className="text-slate-700">{option}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                            
                            <div className="flex justify-between items-center pt-4">
                              <Button variant="outline" onClick={() => setShowQuiz(false)}>
                                Back to Content
                              </Button>
                              <Button 
                                onClick={handleSubmitQuiz}
                                disabled={submittingQuiz}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="submit-quiz-btn"
                              >
                                {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Submit Quiz
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* OTP Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter OTP to Sign</DialogTitle>
            <DialogDescription>
              Enter the 6-digit code sent to your email
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="text"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-[0.3em] font-mono h-14"
              maxLength={6}
              data-testid="handbook-otp-input"
            />
            {debugOtp && (
              <p className="text-center text-slate-400 text-xs mt-2">Debug OTP: {debugOtp}</p>
            )}
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => { setShowOtpModal(false); setOtp(""); }}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#1e3a8a]"
                onClick={handleVerifyOtp}
                disabled={verifyingOtp || otp.length !== 6}
                data-testid="verify-handbook-otp-btn"
              >
                {verifyingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingPortal;
